import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useClasses from "hooks/useClasses";
import { getAnalyticsSummary } from "store/actions/analytics";
import MetricCard from "./MetricCard";
import AnalyticsFilters from "./components/AnalyticsFilters";
import styles from "./analyticsStyle";
import { CircularProgress, Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, Paper } from "@mui/material";
import AppleIcon from "@mui/icons-material/Apple";
import ShopIcon from "@mui/icons-material/Shop";
import { useTheme } from "@mui/material/styles";

export default function AnalyticsPlatform() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const history = useHistory();

  const { summary, analyticsFilters, analyticsLoading } = useSelector((s) => s.analytics);
  const [activeTab, setActiveTab] = useState(0);

  const fetchAll = (filters) => {
    dispatch(getAnalyticsSummary(filters, history));
  };

  useEffect(() => {
    if (analyticsFilters.period !== "custom") {
      fetchAll(analyticsFilters);
    }
  }, [analyticsFilters]);


  const handleRefresh = () => {
    fetchAll(analyticsFilters);
  };

  const s = summary?.data || summary || {};
  const dlIos = s.downloads?.ios || {};
  const dlAnd = s.downloads?.android || {};
  const quality = s.quality || {};
  const plats = s.platforms || [];
  const users = s.users || {};
  const avail = users.availability || {};
  const comp = s.comparison || {};

  if (analyticsLoading && !summary) {
    return <div className={classes.loadingOverlay}><CircularProgress size={32} sx={{ color: "#6366f1", mr: 1.5 }} /> Loading downloads...</div>;
  }

  return (
    <div className={classes.analyticsContainer}>
      <AnalyticsFilters classes={classes} onRefresh={handleRefresh} />

      <Typography variant="h5" style={{ color: isDark ? "#fff" : "#333", marginBottom: 20 }}>Platform Activity</Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} textColor={isDark ? "inherit" : "primary"}>
          <Tab icon={<AppleIcon />} iconPosition="start" label="iOS" />
          <Tab icon={<ShopIcon />} iconPosition="start" label="Android" />
          <Tab label="Quality" />
          <Tab label="Breakdown" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <div className={classes.kpiGrid} style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            <MetricCard name="First Time" value={dlIos.first_time} unit="downloads" availability={avail.ios_first_time} comparison={comp.ios_first_time} />
            <MetricCard name="Redownloads" value={dlIos.redownloads} unit="downloads" availability={avail.ios_redownloads} comparison={comp.ios_redownloads} />
            <MetricCard name="Total Downloads" value={dlIos.total} unit="downloads" availability={avail.ios_total} comparison={comp.ios_total} />
          </div>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <div className={classes.kpiGrid} style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            <MetricCard name="First Time Installers" value={dlAnd.first_time_installers} unit="installs" availability={avail.android_first_time_installers} comparison={comp.android_first_time_installers} />
            <MetricCard name="Install Events" value={dlAnd.install_events} unit="events" availability={avail.android_install_events} comparison={comp.android_install_events} />
            <MetricCard name="Uninstall Events" value={dlAnd.uninstall_events} unit="events" availability={avail.android_uninstall_events} comparison={comp.android_uninstall_events} />
          </div>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {Object.keys(quality).length > 0 ? (
            <Box>
              <div className={classes.kpiGrid} style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
                {Object.keys(quality).filter(k => k !== "availability").map((k) => (
                  <MetricCard key={k} name={k.replace(/_/g, " ")} value={quality[k]} availability={quality.availability?.[k]} comparison={comp[k]} />
                ))}
              </div>

              {quality.availability && Object.keys(quality.availability).length > 0 && (
                <Box mt={4} width="100%">
                  <Typography variant="subtitle2" style={{ color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)", marginBottom: 12 }}>Data Availability Details</Typography>
                  <Paper style={{ background: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb", overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Metric</TableCell>
                          <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Status</TableCell>
                          <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Source</TableCell>
                          <TableCell style={{ color: isDark ? "#fff" : "#333", borderBottom: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(quality.availability).map(([metric, info]) => (
                          <TableRow key={metric}>
                            <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>{metric.replace(/_/g, " ")}</TableCell>
                            <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>{info.availability}</TableCell>
                            <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>{info.source}</TableCell>
                            <TableCell style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>{info.coverage_note || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Box>
              )}
            </Box>
          ) : (
            <Typography style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No quality data available.</Typography>
          )}
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          {plats.length > 0 ? (
            plats.map((plat, idx) => {
              const pName = plat?.platform || "Unknown";
              const nl = pName.toLowerCase();
              return (
                <div key={idx} style={{ marginBottom: "20px" }}>
                  <Typography variant="subtitle1" style={{ color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    {nl.includes("ios") || nl.includes("apple") ? <AppleIcon fontSize="small" /> : null}
                    {nl.includes("android") ? <ShopIcon fontSize="small" style={{ color: "#34d399" }} /> : null}
                    {pName}
                  </Typography>
                  <div className={classes.kpiGrid}>
                    <MetricCard name="Active Users" value={plat?.active_users} unit="users" />
                    <MetricCard name="New Users" value={plat?.new_users} unit="users" />
                    <MetricCard name="Sessions" value={plat?.sessions} unit="sessions" />
                    <MetricCard name="Crashes" value={plat?.crashes} unit="crashes" />
                  </div>
                </div>
              );
            })
          ) : (
            <Typography style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>No platform data available.</Typography>
          )}
        </Box>
      )}
    </div>
  );
}
